// Dagsedler / kalender — kjernen i aktivitetsstyring for HV/totalforsvaret.
// En «Outlook-kalender» som DISTRIBUERES nedover kommandokjeden: distrikt tasker
// et HV-område (grovt), områdesjef detaljerer for tropper, troppsledelse for lag,
// lagfører for makkerpar/enkeltmann. Hvert nivå eier sitt lag av detaljer, koblet
// til foreldre-oppdraget (kaskade). Alt her er REN, deterministisk, testbar logikk;
// persistens + org-scope-distribusjon ligger i hub-en, visning i COP-panelet.
import { z } from 'zod';
import { SecurityLevel } from './classification.js';
// ── Kommandonivå (distribusjonsstige) ───────────────────────────────────────
// Union av echelon (enkeltmann→brigade) + HV-territorielt (område, distrikt).
// Brukes til kaskade: et høyere nivå kan taske et lavere.
export const CommandLevel = z.enum([
    'enkeltmann', 'makkerpar', 'lag', 'tropp', 'kompani', 'område', 'bataljon', 'brigade', 'distrikt',
]);
/** Rang i kommandokjeden (høyere tall = høyere kommando). område≈kompani. */
export const COMMAND_RANK = {
    enkeltmann: 0, makkerpar: 1, lag: 2, tropp: 3, kompani: 4, område: 4, bataljon: 5, brigade: 6, distrikt: 7,
};
export const COMMAND_LABEL = {
    enkeltmann: 'Enkeltmann', makkerpar: 'Makkerpar', lag: 'Lag', tropp: 'Tropp',
    kompani: 'Kompani', område: 'HV-område', bataljon: 'Bataljon', brigade: 'Brigade', distrikt: 'HV-distrikt',
};
export function commandRank(l) { return COMMAND_RANK[l]; }
/** Kan `from` taske (distribuere aktivitet til) `to`? Strengt høyere kommando. */
export function canTaskDown(from, to) {
    return commandRank(from) > commandRank(to);
}
/** Naturlig neste nivå ned i HV-kjeden (til kaskade-standard). */
export function subordinateLevel(l) {
    const chain = ['distrikt', 'område', 'tropp', 'lag', 'makkerpar', 'enkeltmann'];
    const i = chain.indexOf(l);
    if (i >= 0 && i < chain.length - 1)
        return chain[i + 1];
    // Felthær-kjeden (brigade→bataljon→kompani→tropp→lag…).
    const army = ['brigade', 'bataljon', 'kompani', 'tropp', 'lag', 'makkerpar', 'enkeltmann'];
    const j = army.indexOf(l);
    return j >= 0 && j < army.length - 1 ? army[j + 1] : null;
}
// ── Enhets-referanse (hvem eier / er tasket) ────────────────────────────────
export const UnitRefSchema = z.object({
    level: CommandLevel,
    district: z.string().max(64).optional(), // HV-distrikt, f.eks. «HV-01»
    area: z.string().max(120).optional(), // HV-område, f.eks. «Fredrikstad»
    troop: z.string().max(120).optional(),
    lag: z.string().max(120).optional(),
    name: z.string().max(120).optional(), // fri etikett om ønskelig
});
/** Lesbar etikett: «HV-område · HV-01/Fredrikstad». */
export function unitRefLabel(u) {
    if (u.name)
        return u.name;
    const terr = [u.district, u.area, u.troop, u.lag].filter(Boolean).join('/');
    return terr ? `${COMMAND_LABEL[u.level]} · ${terr}` : COMMAND_LABEL[u.level];
}
/** Er `child` innenfor `parent`s territorielle ansvar (og lavere nivå)? */
export function unitWithin(child, parent) {
    if (commandRank(child.level) > commandRank(parent.level))
        return false;
    if (parent.district && child.district && parent.district !== child.district)
        return false;
    if (parent.area && child.area && parent.area !== child.area)
        return false;
    if (parent.troop && child.troop && parent.troop !== child.troop)
        return false;
    if (parent.lag && child.lag && parent.lag !== child.lag)
        return false;
    return true;
}
// ── Aktivitetstyper + status + gjentakelse ──────────────────────────────────
export const ActivityType = z.enum([
    'skyting', 'årlig-trening', 'øvelse', 'kurs', 'vakt', 'sanitet', 'møte',
    'inspeksjon', 'materiell', 'seremoni', 'beredskap', 'annet',
]);
export const ACTIVITY_TYPE_LABEL = {
    skyting: 'Skyting', 'årlig-trening': 'Årlig trening (ÅT)', øvelse: 'Øvelse', kurs: 'Kurs',
    vakt: 'Vakt', sanitet: 'Sanitet', møte: 'Møte', inspeksjon: 'Inspeksjon',
    materiell: 'Materiell', seremoni: 'Seremoni', beredskap: 'Beredskap', annet: 'Annet',
};
export const ACTIVITY_TYPE_COLOR = {
    skyting: '#ff3b3b', 'årlig-trening': '#5b9bd5', øvelse: '#e0a24a', kurs: '#9acd32',
    vakt: '#8a8a8a', sanitet: '#ff8a3d', møte: '#7a7ad5', inspeksjon: '#4bd1c1',
    materiell: '#b08a4a', seremoni: '#d55b9b', beredskap: '#ff3b3b', annet: '#6a6a6a',
};
// forslag→planlagt→beordret (låst nedover)→pågår→fullført; avlyst når som helst.
export const ActivityStatus = z.enum(['forslag', 'planlagt', 'beordret', 'pågår', 'fullført', 'avlyst']);
export const ACTIVITY_STATUS_LABEL = {
    forslag: 'Forslag', planlagt: 'Planlagt', beordret: 'Beordret', pågår: 'Pågår', fullført: 'Fullført', avlyst: 'Avlyst',
};
const STATUS_RANK = { forslag: 0, planlagt: 1, beordret: 2, pågår: 3, fullført: 4, avlyst: 5 };
export const Recurrence = z.enum(['ingen', 'daglig', 'ukentlig', 'månedlig', 'årlig']);
// ── Aktivitet (dagsedel-oppføring) ──────────────────────────────────────────
export const CalendarActivitySchema = z.object({
    id: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    type: ActivityType.default('annet'),
    status: ActivityStatus.default('forslag'),
    startMs: z.number(),
    endMs: z.number(),
    allDay: z.boolean().default(false),
    recurrence: Recurrence.default('ingen'),
    recurrenceUntilMs: z.number().optional(),
    /** Hvem utstedte aktiviteten (nivå + enhet). */
    issuedBy: UnitRefSchema,
    /** Hvem den er tasket til (kan være flere underenheter). */
    assignedTo: z.array(UnitRefSchema).default([]),
    /** Kaskade: id-en til foreldre-oppdraget denne detaljerer. */
    parentId: z.string().max(64).optional(),
    createdBy: z.string().max(128),
    location: z.object({ lat: z.number(), lon: z.number(), label: z.string().max(200).optional() }).optional(),
    description: z.string().max(4000).optional(),
    classification: SecurityLevel.default('UGRADERT'),
    /** Deltakere (personer) med svar-status (Outlook-stil RSVP). */
    attendees: z.array(z.object({
        id: z.string().max(128),
        name: z.string().max(200),
        rsvp: z.enum(['invitert', 'godtatt', 'avslått', 'tentativ']).default('invitert'),
    })).default([]),
    /** Påminnelse: minutter før start (undefined = ingen). */
    reminderMinutes: z.number().int().min(0).max(20160).optional(),
    /** GNÅ-møte: knytter aktiviteten til et samvirkerom (Feltveksler/Matrix). */
    meeting: z.object({
        kind: z.literal('gna'),
        room: z.string().max(200).optional(),
        channel: z.string().max(100).optional(),
    }).optional(),
    tsMs: z.number(),
});
/** Sett/oppdater en deltakers RSVP-svar (returnerer ny aktivitet). */
export function setAttendeeRsvp(a, attendeeId, rsvp) {
    const attendees = a.attendees.map((x) => (x.id === attendeeId ? { ...x, rsvp } : x));
    return { ...a, attendees };
}
/** RSVP-oppsummering (antall pr. status). */
export function rsvpSummary(a) {
    const out = { invitert: 0, godtatt: 0, avslått: 0, tentativ: 0 };
    for (const x of a.attendees)
        out[x.rsvp] = (out[x.rsvp] ?? 0) + 1;
    return out;
}
export function parseCalendarActivity(raw) {
    const r = CalendarActivitySchema.safeParse(raw);
    return r.success ? r.data : null;
}
/**
 * Lag en kaskade-underaktivitet: et lavere nivå detaljerer et foreldre-oppdrag.
 * Arver type/tittel/gradering; nytt nivå + utsteder; status = forslag; peker til
 * foreldre via parentId. `overrides` lar utstederen justere.
 */
export function makeChildActivity(parent, issuedBy, id, createdBy, nowMs, overrides = {}) {
    return CalendarActivitySchema.parse({
        id,
        title: overrides.title ?? parent.title,
        type: overrides.type ?? parent.type,
        status: 'forslag',
        startMs: overrides.startMs ?? parent.startMs,
        endMs: overrides.endMs ?? parent.endMs,
        allDay: parent.allDay,
        recurrence: 'ingen',
        issuedBy,
        assignedTo: overrides.assignedTo ?? [],
        parentId: parent.id,
        createdBy,
        location: overrides.location ?? parent.location,
        description: overrides.description,
        classification: parent.classification,
        tsMs: nowMs,
    });
}
/** Kan en bruker på `userLevel` redigere aktiviteten? Eget nivå + eier eller
 * over; en `beordret` aktivitet fra HØYERE nivå er låst (kan ikke endres nedover
 * — bare detaljeres via underaktiviteter). */
export function canEditActivity(userLevel, a) {
    if (a.status === 'beordret' && commandRank(a.issuedBy.level) > commandRank(userLevel))
        return false;
    return commandRank(userLevel) >= commandRank(a.issuedBy.level);
}
function addStep(ms, rec, n) {
    const d = new Date(ms);
    switch (rec) {
        case 'daglig': return ms + n * 86_400_000;
        case 'ukentlig': return ms + n * 7 * 86_400_000;
        case 'månedlig':
            d.setUTCMonth(d.getUTCMonth() + n);
            return d.getTime();
        case 'årlig':
            d.setUTCFullYear(d.getUTCFullYear() + n);
            return d.getTime();
        default: return ms;
    }
}
/** Materialiser en aktivitets forekomster som overlapper [winStart, winEnd]. */
export function expandRecurrences(a, winStartMs, winEndMs) {
    const dur = Math.max(0, a.endMs - a.startMs);
    if (a.recurrence === 'ingen') {
        if (a.endMs >= winStartMs && a.startMs <= winEndMs)
            return [{ activity: a, startMs: a.startMs, endMs: a.endMs, recurring: false }];
        return [];
    }
    const until = a.recurrenceUntilMs ?? winEndMs;
    const out = [];
    for (let n = 0; n < 2000; n++) {
        const s = addStep(a.startMs, a.recurrence, n);
        if (s > winEndMs || s > until)
            break;
        const e = s + dur;
        if (e >= winStartMs)
            out.push({ activity: a, startMs: s, endMs: e, recurring: n > 0 });
    }
    return out;
}
/** Alle aktivitets-forekomster i [start,end], sortert kronologisk. */
export function activitiesInRange(activities, startMs, endMs) {
    const out = [];
    for (const a of activities)
        out.push(...expandRecurrences(a, startMs, endMs));
    return out.sort((x, y) => x.startMs - y.startMs);
}
/** Aktiviteter relevante for en enhet: tasket direkte til den ELLER til en
 * overordnet enhet den arver fra (f.eks. et distrikts-ÅT gjelder alle områder). */
export function activitiesForUnit(activities, unit) {
    return activities.filter((a) => a.assignedTo.length === 0
        ? unitWithin(unit, a.issuedBy) // ikke eksplisitt tasket → gjelder eiers ansvarsområde
        : a.assignedTo.some((t) => unitWithin(unit, t) || unitWithin(t, unit)));
}
/** Overlappende forekomster for planlegging (samme tidsrom). Par-liste. */
export function detectConflicts(instances) {
    const sorted = [...instances].sort((a, b) => a.startMs - b.startMs);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
            if (sorted[j].startMs >= sorted[i].endMs)
                break; // ingen senere kan overlappe i
            if (sorted[j].activity.id !== sorted[i].activity.id)
                out.push([sorted[i], sorted[j]]);
        }
    }
    return out;
}
/** Grupper forekomster per dag (UTC-dag-indeks), sortert. Til agenda-visning. */
export function agendaByDay(instances, tzOffsetMin = 0) {
    const off = tzOffsetMin * 60_000;
    const byDay = new Map();
    for (const inst of instances) {
        const dayIdx = Math.floor((inst.startMs + off) / 86_400_000);
        if (!byDay.has(dayIdx))
            byDay.set(dayIdx, []);
        byDay.get(dayIdx).push(inst);
    }
    return [...byDay.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([dayIdx, items]) => ({ dayMs: dayIdx * 86_400_000 - off, items: items.sort((x, y) => x.startMs - y.startMs) }));
}
/** Konfliktfri sammenslåing (til hub/CRDT): nyeste status forover, aldri bakover
 * (en `avlyst`/`fullført` kan ikke gjenopplives av en eldre `planlagt`). */
export function mergeActivity(a, b) {
    const newer = b.tsMs >= a.tsMs ? b : a;
    // Status kan bare gå forover (høyeste rang vinner) — hindrer at en eldre
    // «planlagt» gjenoppliver en «avlyst»/«fullført».
    const status = STATUS_RANK[a.status] >= STATUS_RANK[b.status] ? a.status : b.status;
    return { ...newer, status };
}
//# sourceMappingURL=calendar.js.map